import { isNil } from 'lodash-es';

import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';

import type { SearchItemEntity } from '../../redux/entities/normalized/search_item';
import { findOriginalCloudFormat } from '../../redux/files/thunks';
import { getObjectTypeFromAssetOrSearchItem } from '../get_object_type_from_asset_or_search_item';

const downloadableProxyTypes = new Set(['ASSET', 'CUSTOM']);
const downloadableOriginalTypes = new Set([
  'ASSET',
  'CUSTOM',
  'SUBCLIP',
  'NLE_PROJECT',
]);

export function isProxyDownloadable(
  item: SearchItemEntity | AssetsAssetSchemaTypedWithId,
  proxies: FilesProxySchemaWithId[] = [],
) {
  if (getObjectTypeFromAssetOrSearchItem(item) === OBJECT_TYPE_COLLECTIONS) {
    return !item.is_blocked;
  }
  if (isNil(item.type)) {
    return false;
  }
  if (!downloadableProxyTypes.has(item.type)) {
    return false;
  }
  // Check that the asset actually has a closed proxy and that it belongs to this asset. Subclips will point to the parent asset.
  const proxiesValue = 'proxies' in item ? (item.proxies ?? []) : proxies;
  const hasClosedProxies = proxiesValue.some(
    (proxy) => proxy.status === 'CLOSED',
  );
  return !item.is_blocked && hasClosedProxies;
}

export function isOriginalDownloadable(
  item: SearchItemEntity | AssetsAssetSchemaTypedWithId,
  formats: FilesFormatSchemaWithId[] = [],
) {
  const objectType = getObjectTypeFromAssetOrSearchItem(item);
  if (objectType === OBJECT_TYPE_COLLECTIONS) {
    return !item.is_blocked;
  }
  if (isNil(item.type)) {
    return false;
  }
  if (!downloadableOriginalTypes.has(item.type)) {
    return false;
  }
  const formatsValue = 'formats' in item ? (item.formats ?? []) : formats;
  const originalCloudFormat = findOriginalCloudFormat(formatsValue ?? []);
  return !item.is_blocked && originalCloudFormat?.is_online;
}

export function hasDownloadableFormats(
  item: SearchItemEntity | AssetsAssetSchemaTypedWithId,
  formats: FilesFormatSchemaWithIdNullable[] = [],
) {
  const objectType = getObjectTypeFromAssetOrSearchItem(item);
  const formatsValue = 'formats' in item ? (item.formats ?? []) : formats;

  return (
    objectType === OBJECT_TYPE_COLLECTIONS ||
    (formatsValue.length && !item.is_blocked)
  );
}

/**
 * Returns an appropriate item count to show for context menu items. Handles the case when
 * there is one item to download and only one item selected, in this case we return undefined
 * so that there is no number displayed next to the item.
 */
export function getDownloadMenuItemCount({
  downloadItems,
  contextItems,
}: {
  downloadItems: (SearchItemEntity | AssetsAssetSchemaTypedWithId)[];
  contextItems: (SearchItemEntity | AssetsAssetSchemaTypedWithId)[];
}) {
  if (downloadItems.length === 1 && contextItems.length === 1) {
    return undefined;
  }
  return downloadItems.length;
}
